/**
 * Scenarios page component for oil and gas field projects
 */
import {
    Add,
    Bolt,
    Business,
    ContentCopy,
    Delete,
    Description,
    Edit,
    LocalFireDepartment,
    Lock,
    MoreVert,
    OilBarrel,
    Public,
    Search as SearchIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Fab,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';

import { scenariosApi } from '../api/scenarios';
import {
    ScenarioResponse,
    formatDate,
    getFieldTypeLabel,
} from '../types/scenario';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';

// Visibility status UUIDs
const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

const getVisibilityStatusId = (scenario: ScenarioResponse): string | null => {
  const visibilityStatusId = scenario.visibility_status_id;
  const statusIdStr = visibilityStatusId ? String(visibilityStatusId) : null;
  if (!statusIdStr || statusIdStr === 'null' || statusIdStr === 'undefined') {
    return null;
  }
  return statusIdStr;
};

const isScenarioPublic = (scenario: ScenarioResponse): boolean => {
  const statusIdStr = getVisibilityStatusId(scenario);
  return statusIdStr === PUBLIC_STATUS_UUID || (!statusIdStr && !!scenario.is_public);
};

const isScenarioCompanyPublic = (scenario: ScenarioResponse): boolean => {
  const statusIdStr = getVisibilityStatusId(scenario);
  return statusIdStr === COMPANY_PUBLIC_UUID;
};

const getVisibilityChip = (scenario: ScenarioResponse) => {
  // Fallback to is_public if visibility_status_id is not set (for backward compatibility)
  const statusIdStr = getVisibilityStatusId(scenario);

  // If visibility_status_id is not set, use is_public as fallback
  if (!statusIdStr || statusIdStr === 'null' || statusIdStr === 'undefined') {
    if (scenario.is_public) {
      return {
        icon: <Public fontSize="small" />,
        label: 'Public',
        color: 'success' as const
      };
    } else {
      return {
        icon: <Lock fontSize="small" />,
        label: 'Private',
        color: 'default' as const
      };
    }
  }

  const isPublic = statusIdStr === PUBLIC_STATUS_UUID;
  const isCompanyPublic = statusIdStr === COMPANY_PUBLIC_UUID;

  if (isPublic) {
    return {
      icon: <Public fontSize="small" />,
      label: 'Public',
      color: 'success' as const
    };
  } else if (isCompanyPublic) {
    return {
      icon: <Business fontSize="small" />,
      label: 'Company Public',
      color: 'info' as const
    };
  } else {
    return {
      icon: <Lock fontSize="small" />,
      label: 'Private',
      color: 'default' as const
    };
  }
};

export const ScenariosPage: React.FC = () => {
  useRegisterPageHelp('Oil Field Scenarios');
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === UserRole.ADMIN || user?.role === 'admin';
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
  const [copyingScenarioId, setCopyingScenarioId] = useState<string | null>(null);

  // Load scenarios on component mount
  useEffect(() => {
    const loadScenarios = async () => {
      try {
        setLoading(true);
        const response = await scenariosApi.getScenarios();
        setScenarios(response.scenarios);
        setError(null);
      } catch (err) {
        console.error('Failed to load scenarios:', err);
        setError('Failed to load scenarios. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadScenarios();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, scenarioId: string) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedScenario(scenarioId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedScenario(null);
  };

  const handleScenarioClick = (scenario: ScenarioResponse) => {
    navigate(`/app/scenarios/${scenario.id}`);
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    try {
      await scenariosApi.deleteScenario(scenarioId);
      setScenarios(scenarios.filter(s => s.id !== scenarioId));
      handleMenuClose();
    } catch (err) {
      console.error('Failed to delete scenario:', err);
      setError('Failed to delete scenario. Please try again.');
    }
  };

  const handleCopyScenario = async (scenarioId: string) => {
    try {
      setCopyingScenarioId(scenarioId);
      const created = await scenariosApi.copyScenario(scenarioId, {});
      handleMenuClose();
      navigate(`/app/scenarios/${created.id}/edit`);
    } catch (err) {
      console.error('Failed to copy scenario:', err);
      setError('Failed to copy scenario. Please try again.');
    } finally {
      setCopyingScenarioId(null);
    }
  };

  const handleCreateScenario = () => {
    navigate('/app/scenarios/create');
  };

  // Filter scenarios based on search term and visibility
  const filteredScenarios = scenarios.filter((scenario) => {
    // Search filter
    const matchesSearch = searchTerm === '' ||
      scenario.name.toLowerCase().includes(searchTerm.toLowerCase());

    // Visibility filter
    const visibilityStatusId = scenario.visibility_status_id;
    const statusIdStr = visibilityStatusId ? String(visibilityStatusId) : null;

    let matchesVisibility = true;
    if (visibilityFilter !== 'all') {
      if (visibilityFilter === 'public') {
        matchesVisibility = statusIdStr === PUBLIC_STATUS_UUID ||
          (!statusIdStr && scenario.is_public);
      } else if (visibilityFilter === 'company_public') {
        matchesVisibility = statusIdStr === COMPANY_PUBLIC_UUID;
      } else if (visibilityFilter === 'private') {
        matchesVisibility = statusIdStr === PRIVATE_UUID ||
          (!statusIdStr && !scenario.is_public);
      }
    }

    return matchesSearch && matchesVisibility;
  });


  const getFieldTypeIcon = (fieldType: string) => {
    switch (fieldType) {
      case 'oil':
        return <OilBarrel color="primary" />;
      case 'gas':
        return <LocalFireDepartment color="warning" />;
      case 'oil_gas':
        return <Bolt color="secondary" />;
      default:
        return <Description color="inherit" />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Oil Field Scenarios
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and analyze field development projects and economic scenarios
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search scenarios by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300, flexGrow: 1 }}
          size="small"
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by visibility</InputLabel>
          <Select
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value)}
            label="Filter by visibility"
          >
            <MenuItem value="all">All scenarios</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="company_public">Company Public</MenuItem>
            <MenuItem value="private">Private</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Scenarios Grid */}
      {!loading && !error && (
        <>
          {filteredScenarios.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {searchTerm || visibilityFilter !== 'all'
                  ? "No scenarios match your filters"
                  : "No scenarios found"
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchTerm || visibilityFilter !== 'all'
                  ? "Try adjusting your search criteria or filters"
                  : "Create your first oil and gas field scenario to get started"
                }
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredScenarios.map((scenario) => (
            <Grid item xs={12} sm={6} md={4} key={scenario.id}>
              <Card
                onClick={() => handleScenarioClick(scenario)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {getFieldTypeIcon(scenario.field_type)}
                      <Chip
                        label={getFieldTypeLabel(scenario.field_type)}
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      {(() => {
                        const visibilityChip = getVisibilityChip(scenario);
                        return (
                          <Chip
                            icon={visibilityChip.icon}
                            label={visibilityChip.label}
                            size="small"
                            variant="outlined"
                            color={visibilityChip.color}
                          />
                        );
                      })()}
                    </Box>
                    {(() => {
                      const isPublic = isScenarioPublic(scenario);
                      const isCompanyPublic = isScenarioCompanyPublic(scenario);
                      const isFromUserOrg = scenario.organization_id === user?.organization_id;
                      const canEdit = isFromUserOrg || (isPublic && isSuperAdmin);
                      const canDelete = isFromUserOrg || (isPublic && isSuperAdmin);
                      const canCopy = isFromUserOrg || isPublic || isCompanyPublic;
                      const hasAnyAction = canCopy || canEdit || canDelete;

                      if (!hasAnyAction) return null;

                      return (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, scenario.id)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      );
                    })()}
                  </Box>

                  <Typography variant="h6" component="h3" gutterBottom>
                    {scenario.name}
                  </Typography>

                  {/* Forecast Period */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Forecast Period:</strong>{' '}
                      {scenario.to_date
                        ? `Until ${formatDate(scenario.to_date)}`
                        : 'Not set'}
                    </Typography>
                  </Box>

                  {/* Statistics */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Reservoirs
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {scenario.layers_count ?? 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Wells
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {scenario.wells_count ?? 0}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Dates */}
                  <Box sx={{
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5
                  }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Created:</strong> {formatDate(scenario.created_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <strong>Last Updated:</strong> {formatDate(scenario.updated_at)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
              ))}
            </Grid>
          )}

          {/* Results count */}
          {filteredScenarios.length > 0 && (
            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredScenarios.length} of {scenarios.length} scenarios
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Empty state */}
      {!loading && !error && scenarios.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No scenarios found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first oil and gas field scenario to get started
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={handleCreateScenario}>
            Add Scenario
          </Button>
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {(() => {
          const scenario = scenarios.find(s => s.id === selectedScenario);
          if (!scenario) return null;

          const isPublic = isScenarioPublic(scenario);
          const isCompanyPublic = isScenarioCompanyPublic(scenario);
          const isFromUserOrg = scenario.organization_id === user?.organization_id;
          const canEdit = isFromUserOrg || (isPublic && isSuperAdmin);
          const canDelete = isFromUserOrg || (isPublic && isSuperAdmin);
          const canCopy = isFromUserOrg || isPublic || isCompanyPublic;

          return (
            <>
              {canCopy && (
                <MenuItem
                  onClick={() => selectedScenario && handleCopyScenario(selectedScenario)}
                  disabled={copyingScenarioId === selectedScenario}
                >
                  <ListItemIcon>
                    {copyingScenarioId === selectedScenario ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ContentCopy fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText>
                    {copyingScenarioId === selectedScenario ? 'Copying...' : 'Copy'}
                  </ListItemText>
                </MenuItem>
              )}
              {canEdit && (
                <MenuItem onClick={() => {
                  handleMenuClose();
                  if (selectedScenario) {
                    navigate(`/app/scenarios/${selectedScenario}/edit`);
                  }
                }}>
                  <ListItemIcon>
                    <Edit fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  onClick={() => selectedScenario && handleDeleteScenario(selectedScenario)}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <Delete fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              )}
            </>
          );
        })()}
      </Menu>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add scenario"
        onClick={handleCreateScenario}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24
        }}
      >
        <Add />
      </Fab>

    </Box>
  );
};
